import { supabase } from '../helper/supabaseClient';
import { isWithinDateWindow } from '../utils/dates';

function cleanOffer(row) {
  return {
    id: row.id,
    brandName: row.brand_name,
    brandLogoUrl: row.brand_logo_url ?? null,
    offerTitle: row.offer_title,
    offerDescription: row.offer_description ?? null,
    discountCode: row.discount_code ?? null,
    ctaText: row.cta_text ?? null,
    ctaLink: row.cta_link ?? null,
    tierRequired: row.tier_required ?? null,
    category: row.category ?? null,
    imageUrl: row.image_url ?? null,
    startDate: row.start_date ?? null,
    endDate: row.end_date ?? null,
    isActive: Boolean(row.is_active),
    sortOrder: row.sort_order ?? 0,
    createdAt: row.created_at ?? null,
    updatedAt: row.updated_at ?? null,
    campaignLabel: row.campaign_label ?? null,
    codeType: row.code_type ?? null,
    merchantSlug: row.merchant_slug ?? null,
    isFeatured: Boolean(row.is_featured),
    targetScope: row.target_scope ?? null,
    targetCountry: row.target_country ?? null,
    targetCommunityId: row.target_community_id ?? null,
  };
}

function normStr(v) {
  if (v == null) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
}

/**
 * Whether an offer should be shown to a viewer with the given profile fields.
 * Rows with missing/legacy `target_scope` are treated as global.
 */
export function offerMatchesAudience(offer, viewer) {
  const scope = normStr(offer.targetScope)?.toLowerCase() || 'global';
  const userCountry = normStr(viewer.country);
  const userCommunityId = normStr(viewer.communityId);

  if (scope === 'global') return true;

  if (scope === 'country') {
    const need = normStr(offer.targetCountry);
    if (!need) return false;
    if (!userCountry) return false;
    return need.toLowerCase() === userCountry.toLowerCase();
  }

  if (scope === 'community') {
    const need = normStr(offer.targetCommunityId);
    if (!need) return false;
    if (!userCommunityId) return false;
    return need === userCommunityId;
  }

  return false;
}

/** Short label for admin dropdowns (linked offer picker). */
export function formatOfferAudienceHint(offer) {
  const scope = normStr(offer.targetScope)?.toLowerCase() || 'global';
  if (scope === 'global') return '[global]';
  if (scope === 'country') {
    const c = normStr(offer.targetCountry);
    return c ? `[country: ${c}]` : '[country]';
  }
  if (scope === 'community') return '[community]';
  return `[${scope}]`;
}

function tierRank(tier) {
  if (!tier) return 0;
  const t = String(tier).toLowerCase();
  if (t === 'bronze') return 1;
  if (t === 'silver') return 2;
  if (t === 'gold') return 3;
  return 0;
}

/**
 * Fetch active brand offers from Supabase.
 *
 * - Filters by is_active
 * - Filters by start_date/end_date relative to "now" (client)
 * - If `viewer` is an object, filters by target_scope vs user country / community (Profiles)
 * - If `viewer` is omitted or null, does not apply audience filter (e.g. admin linked-offer picker)
 * - Sorted: featured first, then sort_order asc, created_at desc
 *
 * @param {{ country?: string|null, communityId?: string|null }|null|undefined} viewer
 */
export async function getActiveOffers(viewer) {
  const { data, error } = await supabase
    .from('brand_offers')
    .select(
      [
        'id',
        'brand_name',
        'brand_logo_url',
        'offer_title',
        'offer_description',
        'discount_code',
        'cta_text',
        'cta_link',
        'tier_required',
        'category',
        'image_url',
        'start_date',
        'end_date',
        'is_active',
        'sort_order',
        'created_at',
        'updated_at',
        'campaign_label',
        'code_type',
        'merchant_slug',
        'is_featured',
        'target_scope',
        'target_country',
        'target_community_id',
      ].join(',')
    )
    .eq('is_active', true)
    .order('is_featured', { ascending: false })
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: false });

  if (error) {
    const msg = error?.message || 'Unknown error loading offers';
    throw new Error(msg);
  }

  const now = new Date();
  const cleaned = (data ?? []).map(cleanOffer);

  const inWindow = cleaned.filter((o) =>
    isWithinDateWindow(now, { startDate: o.startDate, endDate: o.endDate })
  );

  if (viewer != null) {
    return inWindow.filter((o) => offerMatchesAudience(o, viewer));
  }

  return inWindow;
}

export function getOfferCategories(offers) {
  const set = new Set();
  for (const o of offers || []) {
    const c = typeof o.category === 'string' ? o.category.trim() : '';
    if (c) set.add(c);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

export function getTierRank(tier) {
  return tierRank(tier);
}

